import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme, { C } from './theme';
import Navbar from './components/Navbar';
import AnalyticsTracker from './components/AnalyticsTracker';
import Home from './pages/Home';
import Nekretnine from './pages/Nekretnine';
import Login from './pages/Login';
import Register from './pages/Register';
import CreateAd from './pages/CreateAd';
import EditAd from './pages/EditAd';
import AdDetails from './pages/AdDetails';
import MyAds from './pages/MyAds';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import CreateBusinessAd from './pages/CreateBusinessAd';
import EditBusinessAd from './pages/EditBusinessAd';
import BusinessAds from './pages/BusinessAds';
import BusinessDetails from './pages/BusinessDetails';
import UCP from './pages/UCP';
import Contact from './pages/Contact';
import EULA from './pages/EULA';
import Privacy from './pages/Privacy';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const LandOwnerRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.userType !== 'land_owner') {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ backgroundColor: C.bg, px: 2 }}>
        <Typography variant="h4" sx={{ mb: 2, color: C.danger }}>
          Pristup odbijen
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', maxWidth: 500, color: C.muted }}>
          Samo vlasnici zemljišta mogu pristupiti ovoj stranici.
          <br />
          Vaš tip naloga: {user?.userType === 'business_owner' ? 'Vlasnik biznisa' : 'Investitor'}
        </Typography>
        <Button variant="contained" onClick={() => window.history.back()}>
          Nazad
        </Button>
      </Box>
    );
  }

  return children;
};

const BusinessOwnerRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.userType !== 'business_owner') {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ backgroundColor: C.bg, px: 2 }}>
        <Typography variant="h4" sx={{ mb: 2, color: C.danger }}>
          Pristup odbijen
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', maxWidth: 500, color: C.muted }}>
          Samo vlasnici biznisa mogu pristupiti ovoj stranici.
          <br />
          Vaš tip naloga: {user?.userType === 'land_owner' ? 'Vlasnik zemljišta' : 'Investitor'}
        </Typography>
        <Button variant="contained" onClick={() => window.history.back()}>
          Nazad
        </Button>
      </Box>
    );
  }

  return children;
};

const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/nekretnine" element={<Nekretnine />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/eula" element={<EULA />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/ads/:id" element={<AdDetails />} />
        <Route path="/messages" element={<Messages />} />
        <Route
          path="/create-ad"
          element={
            <LandOwnerRoute>
              <CreateAd />
            </LandOwnerRoute>
          }
        />
        <Route
          path="/edit-ad/:id"
          element={
            <LandOwnerRoute>
              <EditAd />
            </LandOwnerRoute>
          }
        />
        <Route
          path="/my-ads"
          element={
            <PrivateRoute>
              <MyAds />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path="/ucp"
          element={
            <PrivateRoute>
              <UCP />
            </PrivateRoute>
          }
        />
        <Route path="/business" element={<BusinessAds />} />
        <Route path="/business/:id" element={<BusinessDetails />} />
        <Route
          path="/create-business-ad"
          element={
            <BusinessOwnerRoute>
              <CreateBusinessAd />
            </BusinessOwnerRoute>
          }
        />
        <Route
          path="/edit-business-ad/:id"
          element={
            <BusinessOwnerRoute>
              <EditBusinessAd />
            </BusinessOwnerRoute>
          }
        />
      </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <AnalyticsTracker />
            <AppContent />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
